import React from 'react';
import { Autocomplete, TextField } from '@mui/material';

/**
 * SearchableSelect - A searchable dropdown component with optional grouping
 *
 * @param {Array} options - Array of option objects { value, label, groupBy? }
 * @param {string} value - Currently selected value
 * @param {function} onChange - Callback when value changes (receives value string)
 * @param {string} label - Label for the input field
 * @param {string} groupBy - Field name to group by (optional)
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the input is disabled
 */
const SearchableSelect = ({
  options = [],
  value = '',
  onChange,
  label,
  groupBy = null,
  placeholder = 'Type to search...',
  disabled = false,
}) => {
  // Guard against malformed options (null/undefined label or value) coming
  // from API data — these crash MUI's Autocomplete on render/filter.
  const safeOptions = (options || []).filter((opt) => opt && (opt.value != null || opt.label != null));

  // Find the selected option object from value
  const selectedOption = safeOptions.find((opt) => opt.value === value) || null;

  const handleChange = (event, newValue) => {
    // newValue is the option object or null
    if (onChange) {
      onChange(newValue?.value || '');
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {label && <label style={{ display: 'block', marginBottom: '4px' }}>{label}</label>}
      <Autocomplete
        options={safeOptions}
        value={selectedOption}
        onChange={handleChange}
        groupBy={groupBy ? (option) => option[groupBy] || 'Uncategorized' : undefined}
        getOptionLabel={(option) => String(option?.label ?? option?.value ?? '')}
        renderGroup={(params) => (
          <li key={params.key}>
            <div style={{
              position: 'sticky',
              top: '-8px',
              padding: '4px 10px',
              color: '#888',
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
            }}>
              {params.group}
            </div>
            <ul style={{ padding: 0 }}>{params.children}</ul>
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.value === value?.value}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            variant="outlined"
            size="small"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: '4px 8px',
                minHeight: '38px',
              },
              '& .MuiInputBase-input': {
                padding: '4px 0',
                fontSize: '14px',
              }
            }}
          />
        )}
        filterOptions={(options, { inputValue }) => {
          if (!inputValue) return options;
          const lowerInput = inputValue.toLowerCase();
          return options.filter((option) =>
            String(option?.label ?? '').toLowerCase().includes(lowerInput) ||
            String(option?.value ?? '').toLowerCase().includes(lowerInput)
          );
        }}
        // Ensure user must select from list
        freeSolo={false}
        autoHighlight
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        sx={{
          '& .MuiAutocomplete-inputRoot': {
            padding: '2px 8px',
          }
        }}
      />
    </div>
  );
};

export default SearchableSelect;
